import { Reveal } from "./Reveal";
import { CASE_HIGHLIGHTS } from "../lib/data";
import { ShieldCheck, Gavel, FileX, Scale, Lock } from "lucide-react";

const PLACEHOLDERS = [
  { icon: ShieldCheck, type: "Bail Secured", hint: "Anticipatory & regular bail outcomes" },
  { icon: FileX, type: "FIR Quashed", hint: "Petitions allowed under the High Court's inherent power" },
  { icon: Gavel, type: "Appeal Allowed", hint: "Convictions set aside / sentences reduced" },
];

export const CaseHighlights = () => {
  const hasData = CASE_HIGHLIGHTS.length > 0;

  return (
    <section id="outcomes" className="border-t border-[#262626] bg-[#0A0A0A]" data-testid="outcomes-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <Reveal className="lg:col-span-8">
            <span className="overline">Notable Outcomes</span>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-5 text-[#F7F5F2] text-balance">
              A record of results — <span className="italic text-[#C8A24B]">discreetly</span> shared.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4">
            <p className="text-[#8C8C8C] leading-relaxed flex items-start gap-2">
              <Lock size={15} className="text-[#C8A24B] mt-1 shrink-0" strokeWidth={1.6} />
              Bound by client confidentiality, only outcomes are shared — never identifying details.
            </p>
          </Reveal>
        </div>

        {hasData ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#262626]">
            {CASE_HIGHLIGHTS.map((c, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div className="group h-full border-b border-r border-[#262626] p-8 lg:p-10 hover:bg-[#121212] transition-colors duration-500" data-testid={`case-${i}`}>
                  <div className="flex items-center justify-between">
                    <Scale size={22} className="text-[#C8A24B]" strokeWidth={1.5} />
                    <span className="font-mono text-[0.65rem] uppercase tracking-widest text-[#8C8C8C]">{c.year}</span>
                  </div>
                  <h3 className="font-serif text-2xl lg:text-[1.7rem] leading-tight mt-6 text-[#F7F5F2] group-hover:text-[#C8A24B] transition-colors duration-300">{c.type}</h3>
                  {c.offence && (
                    <span className="mt-3 inline-block text-xs font-mono text-[#8C8C8C] border border-[#262626] rounded-full px-3 py-1.5">{c.offence}</span>
                  )}
                  <p className="text-[#8C8C8C] leading-relaxed mt-4">{c.note}</p>
                  <p className="font-mono text-[0.7rem] uppercase tracking-wider text-[#5c5c5c] mt-6 pt-4 border-t border-[#262626]">
                    {c.court} · {c.ref}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 border-t border-l border-[#262626]">
            {PLACEHOLDERS.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="relative h-full border-b border-r border-[#262626] p-8 lg:p-10 overflow-hidden" data-testid={`case-placeholder-${i}`}>
                  <p.icon size={22} className="text-[#C8A24B]" strokeWidth={1.5} />
                  <h3 className="font-serif text-2xl lg:text-3xl mt-6 text-[#F7F5F2]">{p.type}</h3>
                  <p className="text-[#8C8C8C] leading-relaxed mt-3">{p.hint}</p>
                  {/* redacted lines */}
                  <div className="mt-6 space-y-2.5">
                    <div className="h-2.5 w-4/5 rounded-sm bg-[#1c1c1c]" />
                    <div className="h-2.5 w-3/5 rounded-sm bg-[#1c1c1c]" />
                    <div className="h-2.5 w-2/3 rounded-sm bg-[#1c1c1c]" />
                  </div>
                  <span className="mt-6 inline-block font-mono text-[0.65rem] uppercase tracking-[0.2em] text-[#C8A24B] border border-[#262626] rounded-full px-3 py-1.5">
                    Coming soon
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {!hasData && (
          <Reveal delay={0.2}>
            <p className="mt-10 font-mono text-xs text-[#5c5c5c] text-center uppercase tracking-wider">
              Verified case highlights will be published here shortly.
            </p>
          </Reveal>
        )}
      </div>
    </section>
  );
};
